import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { HelpCircle, ChevronDown, ChevronUp, Mail, Menu, X } from 'lucide-react';
import './InfoPage.css';

const HelpCenterPage: React.FC = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const faqs = [
    {
      question: 'What is HomeQuest Tech?',
      answer: 'HomeQuest Tech is an AI-powered construction management platform that automates manual tasks like CAD file processing, site analysis, vendor coordination, budget tracking, and communications. Our platform uses artificial intelligence to handle the administrative burden so you can focus on building.'
    },
    {
      question: 'How does the beta program work?',
      answer: 'We\'re currently accepting 10 founding members into our exclusive beta program. Beta members get 30% off for life, early access to all features, direct input on product development, and priority support. Start with a 2-week free trial to test all features.'
    },
    {
      question: 'What features are included?',
      answer: 'HomeQuest includes: Floor Plans & CAD (AI room detection and selection placement), AI Site Analysis (GIS county integration), AI Communications (call handling, email composition), Smart Vendor Bidding (with performance tracking), Smart Phases & Budget (AI invoice analysis), and AI Photo Intelligence (automated site documentation).'
    },
    {
      question: 'How does the AI handle my CAD files?',
      answer: 'Upload your CAD files (DWG, DXF, or PDF) and our AI automatically detects and labels all rooms (kitchen, bathrooms, bedrooms, etc.). You can then upload your selections and the AI places them in the correct rooms automatically. No more manual data entry!'
    },
    {
      question: 'What is GIS county system integration?',
      answer: 'Our platform connects directly to your county\'s GIS (Geographic Information System) to pull real-time property data, parcel boundaries, zoning information, and calculate setbacks automatically. This ensures accuracy and saves hours of manual research.'
    },
    {
      question: 'How does AI Communications work?',
      answer: 'Our AI assistant handles inbound calls 24/7, schedules appointments, transfers calls to team members, composes emails with proper context, and replies to messages. It understands natural language and can handle complex requests without your intervention.'
    },
    {
      question: 'Can I compare vendor bids?',
      answer: 'Yes! Send bid requests with embedded CAD files and selections. When vendors respond, view all bids side-by-side with line-item comparisons. Our AI analyzes past vendor performance to provide recommendations based on reliability and quality.'
    },
    {
      question: 'How does automated vendor scheduling work?',
      answer: 'The AI monitors your project phases and vendor lead times. As a phase nears completion, it automatically messages vendors to schedule them. The day before, it confirms via message (tries twice), then calls if there\'s no response. Complete automation.'
    },
    {
      question: 'What about invoice processing?',
      answer: 'Upload invoices and our AI automatically analyzes and categorizes them, matches them to budget line items, flags discrepancies, and tracks spending across all project phases. Real-time budget analytics keep you informed.'
    },
    {
      question: 'How do I get started?',
      answer: 'Click "Start Free Trial" to begin your 2-week trial. Credit card required. Test all features, and if you join as a founding member during beta, lock in 30% off for life.'
    },
    {
      question: 'What kind of support do you offer?',
      answer: 'Beta members get priority support via email at support@homequesttech.com. We typically respond within 24 hours. We also offer onboarding assistance to help you get started with the platform.'
    },
    {
      question: 'Is my data secure?',
      answer: 'Absolutely. We use bank-level encryption (AES-256) for all data at rest and in transit. Your project data, CAD files, and communications are stored securely in the cloud with automatic backups and disaster recovery.'
    }
  ];

  return (
    <div className="info-page">
      <motion.nav initial={{ y: -100 }} animate={{ y: 0 }} className="info-nav">
        <div className="nav-container">
          <div className="nav-logo" onClick={() => navigate('/')}>
            <img src="/homequest-logo.svg" alt="HomeQuest Tech" className="logo-image" />
            <div className="logo-tagline">Where Land Meets Logic</div>
          </div>
          <div className={`nav-links ${mobileMenuOpen ? 'mobile-open' : ''}`}>
            <a onClick={() => navigate('/')}>Home</a>
            <a onClick={() => navigate('/features')}>Features</a>
            <a onClick={() => navigate('/pricing')}>Pricing</a>
            <button onClick={() => navigate('/login')} className="nav-login">Sign In</button>
            <button onClick={() => navigate('/pricing')} className="nav-cta">Start Free Trial</button>
          </div>
          <button className="mobile-menu-toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X /> : <Menu />}
          </button>
        </div>
      </motion.nav>

      <section className="info-hero">
        <div className="hero-container">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="hero-badge">
              <HelpCircle className="badge-icon" />
              <span>HELP & SUPPORT</span>
            </div>
            <h1 className="hero-title">
              How Can We <span className="gradient-text">Help?</span>
            </h1>
            <p className="hero-subtitle">
              Find answers to common questions about HomeQuest's features, pricing, and getting started.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="info-content">
        <div className="content-container">
          <h2>Frequently Asked Questions</h2>

          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
              style={{
                marginBottom: '1rem',
                border: '1px solid rgba(59, 130, 246, 0.2)',
                borderRadius: '12px',
                overflow: 'hidden',
                background: 'rgba(31, 41, 55, 0.6)'
              }}
            >
              <button
                onClick={() => setOpenFaq(openFaq === index ? null : index)}
                style={{
                  width: '100%',
                  padding: '1.5rem',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  background: 'transparent',
                  border: 'none',
                  color: '#F9FAFB',
                  fontSize: '1.125rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  textAlign: 'left'
                }}
              >
                {faq.question}
                {openFaq === index ? <ChevronUp style={{ color: '#3B82F6' }} /> : <ChevronDown style={{ color: '#9CA3AF' }} />}
              </button>
              {openFaq === index && (
                <div style={{ padding: '0 1.5rem 1.5rem', color: '#D1D5DB', fontSize: '1.125rem', lineHeight: '1.7' }}>
                  {faq.answer}
                </div>
              )}
            </motion.div>
          ))}

          <div style={{ marginTop: '4rem', padding: '2rem', background: 'rgba(59, 130, 246, 0.1)', borderRadius: '16px', border: '1px solid rgba(59, 130, 246, 0.3)', textAlign: 'center' }}>
            <Mail style={{ width: '48px', height: '48px', color: '#3B82F6', margin: '0 auto 1rem' }} />
            <h3 style={{ color: '#F9FAFB', marginBottom: '1rem' }}>Still Have Questions?</h3>
            <p style={{ color: '#D1D5DB', marginBottom: '1.5rem' }}>
              Our team is here to help! Reach out to us directly.
            </p>
            <button onClick={() => navigate('/contact')} className="cta-button">
              Contact Support
            </button>
          </div>
        </div>
      </section>

      <footer className="info-footer">
        <div className="footer-container">
          <div className="footer-content">
            <p>&copy; 2025 HomeQuest Tech. All rights reserved.</p>
            <div className="footer-links">
              <a onClick={() => navigate('/')}>Home</a>
              <a onClick={() => navigate('/features')}>Features</a>
              <a onClick={() => navigate('/about')}>About</a>
              <a onClick={() => navigate('/contact')}>Contact</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HelpCenterPage;
